import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IGroup,
    IObjectWithKey,
    IPanelProps,
    IRenderFunction, MessageBar, MessageBarType, Panel,
    PanelType, PrimaryButton, Selection, SelectionMode, Spinner, Text, Stack,
    Separator
} from "@fluentui/react";
import { RootState } from "app/store";
import { saveAs } from 'file-saver';
import { fadeIn, panelButtonsStyles } from "global/Application";
import { TGroupByAggregateMethod } from "global/Utility/TSFunctions";
import { useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { MsalTokenSource } from "../../global/DataSources/MsalTokenSource";
import { formatDate } from "../../global/Utility/TSFunctions";
import {
    IBenefit as FAM_IBenefit
} from "../../model/FlexibleAttributeModel";
import { Constants } from "global/Application/Constants";

interface IProps extends TComponentConnectedProps {
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    variant: 'BDR' | 'CBDR';
}


enum EPayload {
    PlanBenefit = 0,
    FeesAndPremiums = 1,
    StopLoss = 2,
    AccountsReceivable = 3,

}

interface IItemData {
    planId?: string;
    benefitId?: string;
    feesAndPremiumId?: string;
    stopLossId?: string;
    accountsReceivableId?: string;
}

interface IItem extends IObjectWithKey {
    entity: string;
    data?: IItemData;
}

interface IViewModel {
    generateChangeSummary: boolean;

}

const ClientBenefitDetailReportWizard = (props: IProps) => {

    const [loading, setLoading] = useState<boolean>(false);
    const { instance } = useMsal();

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [vm, setVm] = useState<IViewModel>({ generateChangeSummary: false });

    const data = useMemo(() => {
        if (!props.isOpen)
            return {
                items: [],
                columns: [],
                groups: []
            };

        const showContext = {
            planBenefit: true,
            feesAndPremiums: true,
            stopLoss: true,
            accountReceivable: true,
        }

        if (props.variant === 'CBDR') {

            showContext.feesAndPremiums = false
            showContext.stopLoss = false
            showContext.accountReceivable = false
        }

        // we need to create the following structure: Group Configuration - Plan - Benefit
        // then we are going to group by : Group Configuration > Plan > Benefit
        const items = [] as IItem[];

        const columns = [] as IColumn[];
        columns.push({
            key: 'entity',
            fieldName: 'entity',
            name: 'Entity',
            minWidth: 250,
            isResizable: true,
        })

        const groups: IGroup[] = [];

        // Plans/Benefits
        // -------------------------------
        {
            const gpName = "Plan/Benefit";

            const gp: IGroup = {
                key: `groupGroupConfiguration-${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid}`,
                count: 0,
                startIndex: 0,
                name: gpName,
                level: 0
            };

            groups.push(gp);

            // grabbing all plans
            props.state.data.json.plans.forEach(ePlan => {
                //groupGroupConfiguration.count = groupGroupConfiguration.count + 1;
                if (ePlan.deactivated)
                    return;

                const groupPlan: IGroup = {
                    key: `groupPlan-${ePlan.id}`,
                    count: 0,
                    startIndex: items.length,
                    name: ePlan.name,
                    level: 1
                };

                if (!gp.children)
                    gp.children = [];

                gp.children?.push(groupPlan);

                let lastBenefit: FAM_IBenefit | null = null;
                // grabbing all benefits for that plan
                ePlan.benefitRefs.forEach(eBenefitRef => {
                    gp.count = gp.count + 1;
                    groupPlan.count = groupPlan.count + 1;

                    const benefit = props.state.data.json.benefits.find(t => t.id === eBenefitRef.id)!;
                    lastBenefit = benefit;

                    items.push({
                        key: `${gpName}-${ePlan.name}-${benefit?.name}`,
                        entity: `${benefit?.name ?? '?'} (${benefit.bcbs_name})`,
                        data: {
                            planId: ePlan.id,
                            benefitId: benefit.id,
                        },
                    });
                });

                // giving plan a chance to export if there was no benefit
                if (!lastBenefit) {
                    gp.count = gp.count + 1;
                    groupPlan.count = groupPlan.count + 1;

                    items.push({
                        key: `${gpName}-${ePlan.name}`,
                        entity: `[For Plan]`,
                        data: {
                            planId: ePlan.id,
                            benefitId: '369c1558-1887-4d77-a0a3-3f81eaf7b680',
                        },
                    });
                }
            });
        }

        // Fees And Premiums
        // -------------------------------
        if (showContext.feesAndPremiums) {
            const gpName = "Fees And Premiums";

            const gp: IGroup = {
                key: `feesAndPremiums`,
                count: 0,
                startIndex: 0,
                name: gpName,
                level: 0
            };

            gp.startIndex = items.length;
            groups.push(gp);

            props.state.data.json.feesAndPremiums.forEach(eFNP => {
                gp.count += 1;

                items.push({
                    key: `fnp-${eFNP.name}`,
                    entity: eFNP.name,
                    data: {
                        feesAndPremiumId: eFNP.id,
                    }
                });

            });
        }

        // Stop Loss
        // -------------------------------
        if (showContext.stopLoss) {
            const gpName = "Stop Loss";

            const gp: IGroup = {
                key: `stopLoss`,
                count: 0,
                startIndex: 0,
                name: gpName,
                level: 0
            };

            gp.startIndex = items.length;
            groups.push(gp);

            props.state.data.json.stopLoss.forEach(eSL => {
                gp.count += 1;

                items.push({
                    key: `stoploss-${eSL.name}`,
                    entity: eSL.name,
                    data: {
                        stopLossId: eSL.id
                    }
                });

            });
        }

        // Accounts Receivable
        // -------------------------------
        if (showContext.accountReceivable) {
            const gpName = "Accounts Receivable";

            const gp: IGroup = {
                key: `accountsReceivables`,
                count: 0,
                startIndex: 0,
                name: gpName,
                level: 0
            };

            gp.startIndex = items.length;
            groups.push(gp);

            props.state.data.json.claimsARs.forEach(eAR => {
                gp.count += 1;

                items.push({
                    key: `accountsReceivable-${eAR.name}`,
                    entity: eAR.name,
                    data: {
                        accountsReceivableId: eAR.id,
                    }
                });
            });
        }

        return {
            items: items,
            columns: columns,
            groups: groups,
        };

    }, [props.isOpen]);

    const selection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {

            },
            selectionMode: SelectionMode.multiple,
        })
    }, [data]);

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!props.isOpen)
            return <></>;

        return <>
            <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { marginBottom: '15px' } }}>
                <Separator />
                <Text>Please select the records to include:</Text>
                {errorMessage &&
                    <>
                        <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage("")}                >
                            {errorMessage}
                        </MessageBar>
                    </>
                }
                <Checkbox
                    label="Generate Change Summary?"
                    checked={vm.generateChangeSummary}
                    onChange={(ev, checked) => {
                        setVm(vm => {
                            return {
                                ...vm,
                                generateChangeSummary: checked ?? false
                            }
                        });
                    }}
                />

            </Stack>

            <DetailsList
                items={data.items}
                columns={data.columns}
                groups={data.groups}
                onShouldVirtualize={(props) => false}
                selectionMode={SelectionMode.multiple}
                selectionPreservedOnEmptyClick={true}
                layoutMode={DetailsListLayoutMode.justified}
                getKey={(item, index) => { return item.key }}
                selection={selection}
                compact={true}
                groupProps={{
                    showEmptyGroups: true,
                }}
            />
        </>;
    }

    // const aggLastString: TGroupByAggregateMethod<IItem> = (prev, current, key, row) => {
    //     return current;
    // };

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (footerProps, defaultRender) => {
        return <div>
            <PrimaryButton text={loading ? 'Generating...' : 'Continue'}
                styles={panelButtonsStyles}
                disabled={loading}
                onClick={async () => {
                    const selectedItems = selection.getSelection() as IItem[];

                    let include = null;
                    if (selectedItems.length > 0)
                        include = selectedItems.map(i => {
                            return i.data;
                        });

                    let snapshotId = props?.state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid;
                    if (!snapshotId)
                        return;

                    try {
                        setLoading(true);

                        let reportType: 'cbdr' | 'bdr' = 'cbdr';

                        if (props.variant === 'BDR')
                            reportType = 'bdr';

                        const response = await dataSource.getBenefitDetailReport(
                            snapshotId,
                            JSON.stringify({
                                ReportType: reportType,
                                GeneratedDate: formatDate(new Date(), "local:MM/dd/yyyy"),
                                Include: include,
                                GenerateChangeSummary: vm.generateChangeSummary
                            })
                        );

                        saveAs(response.blob, response.fileName, { autoBom: true });

                        // var file = new Blob([response.blob], { type: 'application/pdf' });
                        // var fileURL = URL.createObjectURL(file);
                        // window.open(fileURL);


                        setErrorMessage(null);

                        props.onDismiss?.();
                    } catch (err: any) {
                        console.error(err);
                        setErrorMessage(`Failed to generate the document: ${err.message}`);
                    } finally {
                        setLoading(false);
                    }
                }}
            >
                {loading && <Spinner />}
            </PrimaryButton>
            <DefaultButton text='Cancel' onClick={() => {
                setErrorMessage(null);
                props.onCancel?.();
            }} />
        </div>
    }

    let headerText = "Client Benefit Detail Report Wizard";

    if (props.variant === 'BDR')
        headerText = "Benefit Detail Report Wizard";

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        type={PanelType.medium}
        headerText={headerText}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>;
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(ClientBenefitDetailReportWizard)

